import React from 'react';

const formatNumber = (value, decimals = 2) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '–';
  }
  return value.toFixed(decimals);
};

const renderVolumeDiscountEntry = (entry, idx) => {
  if (entry?.type === 'area_curve') {
    return (
      <tr key={idx}>
        <td className="py-0.5 pl-2 text-[10px] text-gray-600">
          └ {entry.job_type} / {entry.materials}: {formatNumber(entry.group_area_sqm, 2)} sqm @ {formatNumber(entry.discount_percent, 2)}% off
        </td>
        <td className="py-0.5 text-right text-[10px] text-gray-600">
          -£{formatNumber(entry.discount_amount)}
        </td>
      </tr>
    );
  }

  return (
    <tr key={idx}>
      <td className="py-0.5 pl-2 text-[10px] text-gray-600">
        └ {entry.tier_range}: {entry.items_in_tier} @ {entry.discount_percent}% off
      </td>
      <td className="py-0.5 text-right text-[10px] text-gray-600">
        £{formatNumber(entry.tier_cost)}
      </td>
    </tr>
  );
};

const DetailedPriceBreakdown = ({ breakdown }) => {
  const isPartnerTier = Boolean(breakdown.partner_tier_details);
  const minChargeThreshold =
    breakdown.min_charge_threshold ?? breakdown.minimum_charge_threshold;
  const minChargeAdjustment =
    breakdown.min_charge_adjustment ?? breakdown.minimum_charge_applied_adjustment;
  const hasPieces = Array.isArray(breakdown.pieces) && breakdown.pieces.length > 0;
  const linearMinimums = breakdown.linear_minimums_applied || [];

  if (hasPieces && breakdown.job_type === 'mounted-boards') {
    const tier = breakdown.tier_pricing || {};
    return (
      <div className="space-y-4">
        <table className="w-full">
          <tbody>
            <tr className="border-b"><td className="py-2">Raw Material Area</td><td className="py-2 text-right">{formatNumber(breakdown.raw_material_area_sqm, 4)} sqm</td></tr>
            <tr className="border-b"><td className="py-2">Physical Material Cost</td><td className="py-2 text-right">£{formatNumber(breakdown.physical_material_cost)}</td></tr>
            <tr className="border-b text-green-600"><td className="py-2">Material Volume Discount</td><td className="py-2 text-right">-£{formatNumber(breakdown.material_volume_discount_amount)}</td></tr>
            <tr className="border-b"><td className="py-2">Handling Charge</td><td className="py-2 text-right">£{formatNumber(breakdown.handling_cost)}</td></tr>
            <tr className="border-b"><td className="py-2">Explicit Finishing</td><td className="py-2 text-right">£{formatNumber(breakdown.finishing_cost)}</td></tr>
            <tr className="border-b"><td className="py-2">Retail Subtotal</td><td className="py-2 text-right">£{formatNumber(breakdown.retail_subtotal_before_multiplier)}</td></tr>
            <tr className="border-b"><td className="py-2">Global Multiplier ({formatNumber(breakdown.global_price_multiplier, 2)}x)</td><td className="py-2 text-right">+£{formatNumber(breakdown.global_uplift_before_min_charge)}</td></tr>
            <tr className="border-b"><td className="py-2">Retail Total</td><td className="py-2 text-right">£{formatNumber(tier.retail_total ?? breakdown.retail_price)}</td></tr>
            {breakdown.min_charge_applied && <tr className="border-b text-orange-600"><td className="py-2">Retail Minimum Adjustment</td><td className="py-2 text-right">+£{formatNumber(breakdown.min_charge_adjustment)}</td></tr>}
            {tier.discount_percent > 0 && <tr className="border-b text-green-600"><td className="py-2">{tier.tier} Discount ({formatNumber(tier.discount_percent)}%)</td><td className="py-2 text-right">-£{formatNumber(tier.discount_amount)}</td></tr>}
            <tr className="bg-green-50 font-bold text-green-800"><td className="py-3">Final Total</td><td className="py-3 text-right">£{formatNumber(tier.final_total ?? breakdown.total_cost)}</td></tr>
          </tbody>
        </table>
        {breakdown.pieces.map((piece) => (
          <table key={piece.piece_number} className="w-full border border-gray-200 rounded-lg">
            <tbody>
              <tr className="bg-gray-100"><th colSpan="2" className="py-2 px-3 text-left">Piece {piece.piece_number}: {piece.dimensions} x {piece.quantity}</th></tr>
              <tr className="border-b"><td className="py-2 px-3">Raw Material Area</td><td className="py-2 px-3 text-right">{formatNumber(piece.raw_area_sqm, 4)} sqm</td></tr>
              {piece.materials.map((material) => <tr key={material.id} className="border-b"><td className="py-2 px-3">{material.name} ({formatNumber(material.price_per_sqm)}/sqm)</td><td className="py-2 px-3 text-right">£{formatNumber(material.total_cost)}</td></tr>)}
              <tr className="border-b text-green-600"><td className="py-2 px-3">Material Volume Discount ({formatNumber(piece.material_volume_discount_percent)}%)</td><td className="py-2 px-3 text-right">-£{formatNumber(piece.material_volume_discount_amount)}</td></tr>
              <tr className="border-b"><td className="py-2 px-3">Handling ({formatNumber(piece.handling_band.premium_per_board)} each x {formatNumber(piece.handling_quantity_factor * 100, 0)}%)</td><td className="py-2 px-3 text-right">£{formatNumber(piece.handling_cost)}</td></tr>
              {piece.finishing_processes.map((finish) => <tr key={finish.id} className="border-b"><td className="py-2 px-3">{finish.name}</td><td className="py-2 px-3 text-right">£{formatNumber(finish.total_cost)}</td></tr>)}
              <tr className="font-semibold"><td className="py-2 px-3">Piece Retail Subtotal</td><td className="py-2 px-3 text-right">£{formatNumber(piece.retail_subtotal)}</td></tr>
            </tbody>
          </table>
        ))}
      </div>
    );
  }

  if (hasPieces) {
    return (
      <div className="space-y-6">
        <table className="w-full">
          <tbody>
            {breakdown.job_type && (
              <tr className="border-b">
                <td className="py-2 font-medium">Job Type</td>
                <td className="py-2 text-right">{breakdown.job_type}</td>
              </tr>
            )}
            <tr className="border-b">
              <td className="py-2">Total Pieces</td>
              <td className="py-2 text-right">{breakdown.total_pieces}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2">Total Items</td>
              <td className="py-2 text-right">{breakdown.total_items}</td>
            </tr>
            {linearMinimums.length > 0 && (
              <tr className="border-b text-blue-700">
                <td className="py-2">
                  Linear Minimum Uplift
                  <span className="block text-xs text-gray-500 normal-case">
                    (billable length per material rounded to 1.0m min)
                  </span>
                </td>
                <td className="py-2 text-right">
                  {linearMinimums.map((entry, idx) => (
                    <div key={idx}>
                      {entry.name || entry.material_id}: +{formatNumber(entry.delta_length_m, 1)}m (£
                      {formatNumber(entry.delta_requested)})
                    </div>
                  ))}
                </td>
              </tr>
            )}
            <tr className="border-b">
              <td className="py-2">Subtotal (before min. charge)</td>
              <td className="py-2 text-right">
                £{formatNumber(breakdown.total_cost_before_min_charge ?? breakdown.sub_total)}
              </td>
            </tr>
            {breakdown.volume_discount_amount > 0 && (
              <tr className="border-b bg-green-50 align-top">
                <td className="py-3">
                  <div className="font-medium text-green-700">Volume Discount Applied</div>
                  <div className="text-xs text-gray-600">
                    Matching pieces update together as grouped area increases.
                  </div>
                  {Array.isArray(breakdown.volume_discount_breakdown) &&
                    breakdown.volume_discount_breakdown.length > 0 && (
                      <table className="mt-2 w-full text-xs">
                        <tbody>
                          {breakdown.volume_discount_breakdown.map((entry, idx) => (
                            renderVolumeDiscountEntry(entry, idx)
                          ))}
                        </tbody>
                      </table>
                    )}
                </td>
                <td className="py-3 text-right">
                  <div className="font-semibold text-green-600">
                    -£{formatNumber(breakdown.volume_discount_amount)}
                  </div>
                  <div className="pt-2 text-sm font-medium text-gray-700">
                    After discount: £
                    {formatNumber(
                      breakdown.subtotal_after_volume_discount ??
                        breakdown.total_cost_before_min_charge ??
                        breakdown.sub_total
                    )}
                  </div>
                </td>
              </tr>
            )}
            {minChargeThreshold !== undefined && minChargeAdjustment !== undefined && (
              <tr className="border-b text-orange-600">
                <td className="py-2">
                  Minimum Charge Adjustment
                  <span className="block text-xs text-gray-500 normal-case">
                    (to reach minimum of £{formatNumber(minChargeThreshold)})
                  </span>
                </td>
                <td className="py-2 text-right">+ £{formatNumber(minChargeAdjustment)}</td>
              </tr>
            )}
            {isPartnerTier ? (
              <>
                <tr className="border-b bg-gray-50 font-semibold">
                  <td className="py-3">Retail Price</td>
                  <td className="py-3 text-right">
                    £{formatNumber(breakdown.partner_tier_details.original_retail_price)}
                  </td>
                </tr>
                <tr className="border-b text-green-600">
                  <td className="py-2">
                    Partner Discount ({breakdown.partner_tier_details.discount_percent}%)
                  </td>
                  <td className="py-2 text-right">
                    -£{formatNumber(
                      breakdown.partner_tier_details.original_retail_price - breakdown.total_cost
                    )}
                  </td>
                </tr>
                <tr className="bg-green-50 font-bold text-green-800">
                  <td className="py-3">Partner Price</td>
                  <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
                </tr>
              </>
            ) : (
              <tr className="bg-gray-50 font-bold">
                <td className="py-3">Final Price</td>
                <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="space-y-4">
          {breakdown.pieces.map((piece, index) => {
            const pieceMaterials = piece.materials ?? [];
            const pieceFinishes = piece.finishing_processes ?? [];
            const pieceMinChargeThreshold =
              piece.min_charge_threshold ?? piece.minimum_charge_threshold;
            const pieceMinChargeAdjustment =
              piece.min_charge_adjustment ?? piece.minimum_charge_applied_adjustment;
            const pieceSubtotal = piece.sub_total_before_min_charge ?? piece.piece_cost;
            const pieceRetail =
              typeof pieceSubtotal === 'number' && !Number.isNaN(pieceSubtotal)
                ? pieceSubtotal / 0.625
                : null;

            return (
              <table
                key={piece.piece_number ?? index}
                className="w-full border border-gray-200 rounded-lg"
              >
                <thead>
                  <tr className="bg-gray-100">
                    <th colSpan="2" className="py-2 px-3 text-left font-semibold">
                      Piece {piece.piece_number ?? index + 1}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-3">Job Type</td>
                    <td className="py-2 px-3 text-right">{piece.job_type || '—'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3">Dimensions</td>
                    <td className="py-2 px-3 text-right">{piece.dimensions || '—'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3">Quantity</td>
                    <td className="py-2 px-3 text-right">{piece.quantity ?? '—'}</td>
                  </tr>
                  {typeof piece.area_used_for_pricing_sqm === 'number' && (
                    <tr className="border-b">
                      <td className="py-2 px-3">Area Used for Pricing</td>
                      <td className="py-2 px-3 text-right">
                        {formatNumber(piece.area_used_for_pricing_sqm)} sqm
                      </td>
                    </tr>
                  )}

                  {pieceMaterials.length > 0 && (
                    <>
                      <tr className="border-b bg-gray-50">
                        <td colSpan="2" className="py-2 px-3 font-medium">
                          Materials
                        </td>
                      </tr>
                    {pieceMaterials.map((material, materialIndex) => (
                      <tr key={materialIndex} className="border-b">
                        <td className="py-2 px-3 pl-6">
                          <div className="font-medium">{material.name}</div>
                          {material.pricing_method === 'linear_m' && (
                            <div className="text-xs text-gray-500">
                              Linear: {formatNumber(material.length_m_used ?? material.length_m, 3)}m
                              {material.planned_length_m !== undefined && (
                                <span>
                                  {' '}
                                  (planned {formatNumber(material.planned_length_m, 3)}m)
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(material.total_cost)}
                        </td>
                      </tr>
                    ))}
                      <tr className="border-b">
                        <td className="py-2 px-3">Total Materials Cost</td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(piece.materials_cost)}
                        </td>
                      </tr>
                    </>
                  )}

                  {piece.print_cost > 0 && (
                    <>
                      <tr className="border-b bg-gray-50">
                        <td colSpan="2" className="py-2 px-3 font-medium">
                          Print Costs
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 pl-6">Setup Cost</td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(piece.print_setup_cost)}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 pl-6">Run Cost</td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(piece.print_run_cost)}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3">Total Print Cost</td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(piece.print_cost)}
                        </td>
                      </tr>
                    </>
                  )}

                  {pieceFinishes.length > 0 && (
                    <>
                      <tr className="border-b bg-gray-50">
                        <td colSpan="2" className="py-2 px-3 font-medium">
                          Finishing Processes
                        </td>
                      </tr>
                      {pieceFinishes.map((finish, finishIndex) => (
                        <tr key={finishIndex} className="border-b">
                          <td className="py-2 px-3 pl-6">{finish.name}</td>
                          <td className="py-2 px-3 text-right">
                            £{formatNumber(finish.total_cost)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-b">
                        <td className="py-2 px-3">Total Finishing Cost</td>
                        <td className="py-2 px-3 text-right">
                          £{formatNumber(piece.finishing_cost)}
                        </td>
                      </tr>
                    </>
                  )}

                  {/* Volume Discount for this piece */}
                  {piece.volume_discount_amount > 0 && (
                    <>
                      <tr className="border-b bg-green-50">
                        <td colSpan="2" className="py-2 px-3">
                          <div className="font-medium text-green-700 text-sm mb-1">Volume Discount Applied</div>
                          <table className="w-full text-xs">
                            <tbody>
                              <tr>
                                <td className="py-0.5 text-gray-700">Total Savings:</td>
                                <td className="py-0.5 text-right text-green-600 font-semibold">
                                  -£{formatNumber(piece.volume_discount_amount)}
                                </td>
                              </tr>

                              {piece.volume_discount_breakdown && piece.volume_discount_breakdown.length > 0 && (
                                <>
                                  <tr>
                                    <td colSpan="2" className="pt-1 pb-0.5 text-[10px] text-gray-600">
                                      Discount Breakdown:
                                    </td>
                                  </tr>
                                  {piece.volume_discount_breakdown.map((entry, idx) => (
                                    renderVolumeDiscountEntry(entry, idx)
                                  ))}
                                </>
                              )}

                              <tr className="border-t border-green-200">
                                <td className="pt-1 text-gray-700 font-medium">
                                  After volume discount:
                                </td>
                                <td className="pt-1 text-right font-medium">
                                  £{formatNumber(piece.subtotal_after_volume_discount)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </>
                  )}

                  <tr className="border-b font-semibold">
                    <td className="py-2 px-3">Piece Subtotal</td>
                    <td className="py-2 px-3 text-right">
                      £{formatNumber(pieceSubtotal)}
                      {isPartnerTier && pieceRetail !== null && (
                        <span className="text-red-600 font-medium ml-2">
                          (Retail: £{formatNumber(pieceRetail)})
                        </span>
                      )}
                    </td>
                  </tr>

                  {pieceMinChargeThreshold !== undefined &&
                    pieceMinChargeAdjustment !== undefined && (
                      <tr className="border-b text-orange-600">
                        <td className="py-2 px-3">
                          Minimum Charge Adjustment
                          <span className="block text-xs text-gray-500 normal-case">
                            (to reach minimum of £{formatNumber(pieceMinChargeThreshold)})
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right">
                          + £{formatNumber(pieceMinChargeAdjustment)}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2">Component</th>
          <th className="text-right py-2">Value</th>
        </tr>
      </thead>
      <tbody>
        {breakdown.job_type && (
          <tr className="border-b">
            <td className="py-2">Job Type</td>
            <td className="text-right">{breakdown.job_type}</td>
          </tr>
        )}
        <tr className="border-b">
          <td className="py-2">Dimensions</td>
          <td className="text-right">{breakdown.dimensions}</td>
        </tr>
        <tr className="border-b">
          <td className="py-2">Quantity</td>
          <td className="text-right">{breakdown.quantity}</td>
        </tr>
        {typeof breakdown.area_used_for_pricing_sqm === 'number' && (
          <>
            <tr className="border-b">
              <td className="py-2">Area Used for Pricing</td>
              <td className="text-right">
                {formatNumber(breakdown.area_used_for_pricing_sqm)} sqm
              </td>
            </tr>
            {typeof breakdown.total_effective_area_sqm === 'number' && (
              <tr className="border-b">
                <td className="py-2 pl-4 text-xs text-gray-600">
                  Calculated Effective Area (with gutter)
                </td>
                <td className="text-right text-xs text-gray-600">
                  {formatNumber(breakdown.total_effective_area_sqm, 4)} sqm
                </td>
              </tr>
            )}
            {typeof breakdown.standard_gutter_mm === 'number' && (
              <tr className="border-b">
                <td className="py-2 pl-4 text-xs text-gray-600">Standard Gutter Applied</td>
                <td className="text-right text-xs text-gray-600">
                  {formatNumber(breakdown.standard_gutter_mm, 0)} mm per side
                </td>
              </tr>
            )}
          </>
        )}

        {typeof breakdown.job_setup_charge === 'number' && (
          <tr className="border-b">
            <td className="py-2 font-medium">Job Setup Charge</td>
            <td className="text-right font-medium">
              £{formatNumber(breakdown.job_setup_charge)}
            </td>
          </tr>
        )}

        {Array.isArray(breakdown.materials) && breakdown.materials.length > 0 && (
          <>
            <tr className="border-b bg-gray-50">
              <td colSpan="2" className="py-2 font-medium">
                Materials
              </td>
            </tr>
            {breakdown.materials.map((material, index) => (
              <tr key={index} className="border-b">
                <td className="py-2 pl-4">{material.name}</td>
                <td className="py-2 text-right">£{formatNumber(material.total_cost)}</td>
              </tr>
            ))}
            <tr className="border-b">
              <td className="py-2">Total Materials Cost</td>
              <td className="py-2 text-right">£{formatNumber(breakdown.materials_cost)}</td>
            </tr>
          </>
        )}

        {breakdown.print_cost > 0 && (
          <>
            <tr className="border-b bg-gray-50">
              <td colSpan="2" className="py-2 font-medium">
                Print Costs
              </td>
            </tr>
            <tr className="border-b">
              <td className="py-2 pl-4">Setup Cost</td>
              <td className="py-2 text-right">£{formatNumber(breakdown.print_setup_cost)}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 pl-4">Run Cost</td>
              <td className="py-2 text-right">£{formatNumber(breakdown.print_run_cost)}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2">Total Print Cost</td>
              <td className="py-2 text-right">£{formatNumber(breakdown.print_cost)}</td>
            </tr>
          </>
        )}

        {Array.isArray(breakdown.finishing_processes) &&
          breakdown.finishing_processes.length > 0 && (
            <>
              <tr className="border-b bg-gray-50">
                <td colSpan="2" className="py-2 font-medium">
                  Finishing Processes
                </td>
              </tr>
              {breakdown.finishing_processes.map((process, index) => (
                <tr key={index} className="border-b">
                  <td className="py-2 pl-4">{process.name}</td>
                  <td className="py-2 text-right">£{formatNumber(process.total_cost)}</td>
                </tr>
              ))}
              <tr className="border-b">
                <td className="py-2">Total Finishing Cost</td>
                <td className="py-2 text-right">£{formatNumber(breakdown.finishing_cost)}</td>
              </tr>
            </>
          )}

        {typeof breakdown.sub_total_before_min_charge === 'number' && (
          <tr className="border-b font-semibold">
            <td className="py-2">Subtotal (before min. charge)</td>
            <td className="py-2 text-right">
              £{formatNumber(breakdown.sub_total_before_min_charge)}
            </td>
          </tr>
        )}

        {/* Volume Discount Section */}
        {breakdown.volume_discount_amount > 0 && (
          <>
            <tr className="border-b bg-green-50">
              <td colSpan="2" className="py-3 px-3">
                <div className="font-semibold text-green-700 mb-2">Volume Discount Applied</div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-gray-700 font-medium">Total Savings:</td>
                      <td className="py-1 text-right text-green-600 font-semibold">
                        -£{formatNumber(breakdown.volume_discount_amount)}
                      </td>
                    </tr>

                    {breakdown.volume_discount_breakdown && breakdown.volume_discount_breakdown.length > 0 && (
                      <>
                        <tr>
                          <td colSpan="2" className="pt-2 pb-1 text-xs text-gray-600">
                            Discount Breakdown:
                          </td>
                        </tr>
                        {breakdown.volume_discount_breakdown.map((entry, idx) => (
                          renderVolumeDiscountEntry(entry, idx)
                        ))}
                      </>
                    )}

                    <tr className="border-t border-green-200">
                      <td className="pt-2 text-gray-700 font-medium">
                        Subtotal after volume discount:
                      </td>
                      <td className="pt-2 text-right font-medium">
                        £{formatNumber(breakdown.subtotal_after_volume_discount || breakdown.sub_total_before_min_charge)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </>
        )}

        {minChargeThreshold !== undefined && minChargeAdjustment !== undefined && (
          <tr className="border-b text-orange-600">
            <td className="py-2">
              Minimum Charge Adjustment
              <span className="block text-xs text-gray-500 normal-case">
                (to reach minimum of £{formatNumber(minChargeThreshold)})
              </span>
            </td>
            <td className="py-2 text-right">+ £{formatNumber(minChargeAdjustment)}</td>
          </tr>
        )}

        {breakdown.trade_discount_amount > 0 && (
          <tr className="border-b">
            <td className="py-2">Trade Discount ({breakdown.trade_discount_percent}%)</td>
            <td className="py-2 text-right">-£{formatNumber(breakdown.trade_discount_amount)}</td>
          </tr>
        )}

        {isPartnerTier ? (
          <>
            <tr className="border-b bg-gray-50 font-semibold">
              <td className="py-3">Retail Price</td>
              <td className="py-3 text-right">
                £{formatNumber(breakdown.partner_tier_details.original_retail_price)}
              </td>
            </tr>
            <tr className="border-b text-green-600">
              <td className="py-2">
                Partner Discount ({breakdown.partner_tier_details.discount_percent}%)
              </td>
              <td className="py-2 text-right">
                -£{formatNumber(
                  breakdown.partner_tier_details.original_retail_price - breakdown.total_cost
                )}
              </td>
            </tr>
            <tr className="bg-green-50 font-bold text-green-800">
              <td className="py-3">Partner Price</td>
              <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
            </tr>
          </>
        ) : (
          <tr className="bg-gray-50 font-bold">
            <td className="py-3">Final Price</td>
            <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default DetailedPriceBreakdown;
